import { Component, Input, forwardRef, OnInit, OnDestroy, ViewChild, ElementRef, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { Subscription, firstValueFrom } from 'rxjs';

// 服務導入
import { FileUploadService } from '../../../services/file-upload.service';
import { GuidService } from '../../../services/guid.service';
import { OpanaiService } from '../../../services/openai.service';
import { I18nService } from '../../../services/i18n.service';
import { environment } from '../../../services/global-environment';

// 介面導入
import { RequiredDocumentConfig, DEFAULT_REQUIRED_DOCUMENT_CONFIG } from './models/required-document-config.interface';
import { DocumentFileItem, DocumentAIParseResult, RequiredDocumentValue } from './models/document-file-item.interface';

// 管道導入
import { TranslatePipe } from '../../../pipes/translate.pipe';

@Component({
  selector: 'app-form-required-document-upload',
  standalone: true,
  imports: [CommonModule, FormsModule, HttpClientModule, TranslatePipe],
  template: `
    <div class="required-document-upload-container">
      <!-- 證件標題和說明 -->
      <div class="document-header">
        <label [for]="id" [class.required-field]="required" class="document-title">
          <i class="fas fa-file-alt me-2"></i>
          {{ config.documentName || label | translate }}
          <span class="required-indicator" *ngIf="required">*</span>
        </label>
        <p class="document-description" *ngIf="config.documentDescription">
          {{ config.documentDescription | translate }}
        </p>
      </div>

      <!-- 檔案上傳區域（未上傳時顯示） -->
      <div class="document-upload-area"
           *ngIf="!uploadedFile"
           [class.drag-over]="isDragOver"
           [class.disabled]="disabled"
           (dragover)="onDragOver($event)"
           (dragleave)="onDragLeave($event)"
           (drop)="onFileDrop($event)"
           (click)="triggerFileSelect()">

        <div class="upload-content">
          <div class="upload-icon">
            <i class="fas fa-cloud-upload-alt"></i>
          </div>
          <div class="upload-text">
            <h6>{{ 'required_document.click_or_drag_upload' | translate }} {{ config.documentName }}</h6>
            <small class="upload-hint">
              {{ getUploadHint() }}
            </small>
          </div>
        </div>

        <input #fileInput
               type="file"
               [id]="id"
               [name]="name"
               (change)="onFileSelected($event)"
               [accept]="getAcceptedTypes()"
               hidden>
      </div>

      <!-- 已上傳檔案顯示區域 -->
      <div class="uploaded-document" *ngIf="uploadedFile">
        <!-- 檔案資訊 -->
        <div class="document-info" [class.uploading]="uploadedFile.uploading" [class.error]="uploadedFile.error">
          <div class="file-preview" *ngIf="config.enablePreview && uploadedFile.previewUrl && !uploadedFile.error">
            <img [src]="uploadedFile.previewUrl"
                 [alt]="uploadedFile.name"
                 class="preview-image"
                 (error)="onPreviewError($event)">
          </div>

          <div class="file-icon" *ngIf="!config.enablePreview || !uploadedFile.previewUrl || uploadedFile.error">
            <i [class]="getFileIcon(uploadedFile.type)"></i>
          </div>

          <div class="file-details">
            <div class="file-name-container">
              <span class="file-name"
                    [class.clickable]="uploadedFile.viewUrl && !uploadedFile.uploading && !uploadedFile.error"
                    (click)="viewFile()">
                {{ uploadedFile.name }}
              </span>
              <div class="file-status">
                <span class="upload-status" *ngIf="uploadedFile.uploading">
                  <i class="fas fa-spinner fa-spin me-1"></i>
                  {{ 'required_document.uploading' | translate }}
                </span>
                <span class="upload-status success" *ngIf="!uploadedFile.uploading && !uploadedFile.error">
                  <i class="fas fa-check me-1"></i>
                  {{ 'required_document.upload_success' | translate }}
                </span>
                <span class="upload-status error" *ngIf="uploadedFile.error">
                  <i class="fas fa-exclamation-triangle me-1"></i>
                  {{ uploadedFile.error }}
                </span>
              </div>
            </div>

            <div class="file-meta" *ngIf="(config.showFileSize || config.showUploadTime) && !uploadedFile.uploading">
              <span class="file-size" *ngIf="config.showFileSize">
                {{ formatFileSize(uploadedFile.size) }}
              </span>
              <span class="upload-time" *ngIf="config.showUploadTime && uploadedFile.uploadedAt">
                {{ formatUploadTime(uploadedFile.uploadedAt) }}
              </span>
            </div>
          </div>

          <!-- 操作按鈕 -->
          <div class="file-actions" *ngIf="!uploadedFile.uploading">
            <button type="button"
                    class="btn btn-sm btn-outline-primary"
                    *ngIf="uploadedFile.viewUrl && !uploadedFile.error"
                    (click)="viewFile()"
                    [title]="'required_document.view_file' | translate">
              <i class="fas fa-eye"></i>
            </button>

            <button type="button"
                    class="btn btn-sm btn-outline-secondary"
                    (click)="replaceFile()"
                    [title]="'required_document.replace_file' | translate">
              <i class="fas fa-redo"></i>
            </button>

            <button type="button"
                    class="btn btn-sm btn-outline-danger"
                    (click)="removeFile()"
                    [title]="'required_document.remove_file' | translate">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>

        <!-- 上傳進度條 -->
        <div class="upload-progress" *ngIf="uploadedFile.uploading && uploadedFile.uploadProgress !== undefined">
          <div class="progress">
            <div class="progress-bar"
                 [style.width.%]="uploadedFile.uploadProgress"
                 [attr.aria-valuenow]="uploadedFile.uploadProgress">
              {{ uploadedFile.uploadProgress }}%
            </div>
          </div>
        </div>

        <!-- AI 解析區域 -->
        <div class="ai-parsing-section" *ngIf="config.aiParsing && uploadedFile && !uploadedFile.uploading && !uploadedFile.error">
          <div class="ai-status">
            <div class="status-header">
              <i class="fas fa-robot me-2"></i>
              <span>{{ 'required_document.ai_parsing' | translate }}</span>
            </div>

            <div class="status-content" [ngSwitch]="aiParseResult?.status">
              <!-- 解析中 -->
              <div *ngSwitchCase="'parsing'" class="parsing-status">
                <i class="fas fa-spinner fa-spin me-2"></i>
                {{ 'required_document.ai_parsing_in_progress' | translate }}
                <div class="ai-progress" *ngIf="aiParseResult?.progress !== undefined">
                  <small>{{ aiParseResult?.progress }}%</small>
                </div>
              </div>

              <!-- 解析完成 -->
              <div *ngSwitchCase="'completed'" class="parsing-completed">
                <i class="fas fa-check-circle text-success me-2"></i>
                {{ 'required_document.ai_parsing_completed' | translate }}

                <div class="extracted-data" *ngIf="aiParseResult?.expiryDate">
                  <div class="extracted-item">
                    <label>{{ 'required_document.expiry_date' | translate }}:</label>
                    <span class="extracted-value">{{ aiParseResult?.expiryDate }}</span>
                    <small class="confidence" *ngIf="aiParseResult?.confidence">
                      ({{ 'required_document.confidence' | translate }}: {{ ((aiParseResult?.confidence || 0) * 100) | number:'1.0-0' }}%)
                    </small>
                  </div>
                </div>

                <div class="ai-actions">
                  <button type="button"
                          class="btn btn-sm btn-outline-primary"
                          (click)="reparseDocument()"
                          [title]="'required_document.reparse' | translate">
                    <i class="fas fa-sync"></i>
                    {{ 'required_document.reparse' | translate }}
                  </button>
                </div>
              </div>

              <!-- 解析失敗 -->
              <div *ngSwitchCase="'failed'" class="parsing-failed">
                <i class="fas fa-exclamation-circle text-danger me-2"></i>
                {{ 'required_document.ai_parsing_failed' | translate }}
                <div class="error-message" *ngIf="aiParseResult?.error">
                  <small>{{ aiParseResult?.error }}</small>
                </div>

                <div class="ai-actions">
                  <button type="button"
                          class="btn btn-sm btn-outline-primary"
                          (click)="reparseDocument()"
                          [title]="'required_document.retry_parsing' | translate">
                    <i class="fas fa-redo"></i>
                    {{ 'required_document.retry_parsing' | translate }}
                  </button>
                </div>
              </div>

              <!-- 預設狀態：顯示開始解析按鈕 -->
              <div *ngSwitchDefault class="parsing-ready">
                <button type="button"
                        class="btn btn-sm btn-primary"
                        (click)="startAIParsing()"
                        [title]="'required_document.start_ai_parsing' | translate">
                  <i class="fas fa-robot me-1"></i>
                  {{ 'required_document.start_ai_parsing' | translate }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RequiredDocumentUploadComponent),
      multi: true
    }
  ],
  styleUrls: ['./required-document-upload.component.scss']
})
export class RequiredDocumentUploadComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() config: RequiredDocumentConfig = { ...DEFAULT_REQUIRED_DOCUMENT_CONFIG };

  @Output() fileUploaded = new EventEmitter<DocumentFileItem>();
  @Output() fileRemoved = new EventEmitter<void>();
  @Output() aiParsingCompleted = new EventEmitter<DocumentAIParseResult>();

  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  uploadedFile?: DocumentFileItem;
  aiParseResult?: DocumentAIParseResult;
  isDragOver = false;

  private subscriptions: Subscription[] = [];
  private companyId = environment.companyid;

  // ControlValueAccessor callbacks
  onChange = (value: RequiredDocumentValue | null) => {};
  onTouched = () => {};

  constructor(
    private fileUploadService: FileUploadService,
    private guidService: GuidService,
    private openaiService: OpanaiService,
    private i18nService: I18nService
  ) {}

  ngOnInit(): void {
    // 合併配置
    this.config = { ...DEFAULT_REQUIRED_DOCUMENT_CONFIG, ...this.config };

    console.log('RequiredDocumentUpload 初始化:', {
      config: this.config,
      id: this.id,
      name: this.name
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // ControlValueAccessor implementation
  writeValue(value: RequiredDocumentValue | null): void {
    if (value?.file) {
      this.uploadedFile = value.file;
      this.aiParseResult = value.aiParseResult;
    } else {
      this.uploadedFile = undefined;
      this.aiParseResult = undefined;
    }
  }

  registerOnChange(fn: (value: RequiredDocumentValue | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // 檔案處理方法
  triggerFileSelect(): void {
    if (this.disabled) return;
    this.fileInputRef.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
      input.value = '';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.disabled && this.config.enableDragDrop) {
      this.isDragOver = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (this.disabled || !this.config.enableDragDrop) return;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  private async handleFile(file: File): Promise<void> {
    if (!this.validateFile(file)) return;

    // 如果已有檔案，先移除
    if (this.uploadedFile) {
      await this.removeExistingFile();
    }

    const fileItem: DocumentFileItem = {
      id: this.guidService.generateGuid(),
      name: file.name,
      originalName: file.name,
      blobName: '',
      size: file.size,
      type: file.type || this.getFileTypeFromExtension(file.name),
      uploadedAt: new Date(),
      uploading: true,
      uploadProgress: 0
    };

    this.uploadedFile = fileItem;
    this.aiParseResult = undefined;
    this.emitChange();

    // 開始上傳
    await this.uploadFile(file, fileItem);
  }

  private validateFile(file: File): boolean {
    // 檔案大小檢查
    if (this.config.maxFileSize && file.size > this.config.maxFileSize * 1024 * 1024) {
      alert(`檔案大小不能超過 ${this.config.maxFileSize}MB`);
      return false;
    }

    // 檔案類型檢查
    if (this.config.acceptedFormats && this.config.acceptedFormats.length > 0) {
      const fileExtension = this.getFileExtension(file.name);
      if (!this.config.acceptedFormats.includes(fileExtension)) {
        alert(`不支援的檔案類型: ${fileExtension}`);
        return false;
      }
    }

    return true;
  }

  private async uploadFile(file: File, fileItem: DocumentFileItem): Promise<void> {
    const uploadSub = this.fileUploadService.uploadFile(file).subscribe({
      next: (event) => {
        if (event.type === 'progress') {
          fileItem.uploadProgress = event.progress;
          this.emitChange();
        } else if (event.type === 'success') {
          fileItem.uploading = false;
          fileItem.blobName = event.response.data.blobName;
          fileItem.uploadedAt = new Date();

          // 產生查看和下載連結
          this.generateFileUrls(fileItem);

          this.fileUploaded.emit(fileItem);
          this.emitChange();

          // 如果啟用 AI 解析，自動開始解析
          if (this.config.aiParsing) {
            setTimeout(() => this.startAIParsing(), 1000);
          }
        }
      },
      error: (error) => {
        fileItem.uploading = false;
        fileItem.error = '上傳失敗: ' + (error.message || '未知錯誤');
        this.emitChange();
      }
    });

    this.subscriptions.push(uploadSub);
  }

  private generateFileUrls(fileItem: DocumentFileItem): void {
    if (!fileItem.blobName) return;

    // 產生查看連結
    const viewSub = this.fileUploadService.generateViewUrl(fileItem.blobName).subscribe({
      next: (response) => {
        fileItem.viewUrl = response.url || response.data?.viewUrl;
        this.emitChange();
      },
      error: (error) => console.error('產生查看連結失敗:', error)
    });

    // 產生下載連結
    const downloadSub = this.fileUploadService.generateDownloadUrl(fileItem.blobName).subscribe({
      next: (response) => {
        fileItem.downloadUrl = response.url || response.data?.downloadUrl;

        if (this.config.enablePreview && this.isImageFile(fileItem.type)) {
          fileItem.previewUrl = fileItem.downloadUrl;
        }
        this.emitChange();
      },
      error: (error) => console.error('產生下載連結失敗:', error)
    });

    this.subscriptions.push(viewSub, downloadSub);
  }

  // AI 解析方法
  async startAIParsing(): Promise<void> {
    if (!this.uploadedFile || !this.config.aiParsing) return;

    this.aiParseResult = {
      status: 'parsing',
      progress: 0
    };
    this.emitChange();

    try {
      // 準備 AI 解析的提示詞
      const prompt = `請分析這個${this.config.documentName}證件並提取以下資訊：
1. 有效期限或到期日期
2. 證件編號（如有）
3. 發證機關（如有）
4. 持證人姓名（如有）

請以 JSON 格式回傳結果，包含：
{
  "expiryDate": "YYYY-MM-DD 格式的有效期限",
  "certificateNumber": "證件編號",
  "issuer": "發證機關",
  "holderName": "持證人姓名",
  "confidence": 0.95
}

如果無法識別某項資訊，請將該欄位設為 null。`;

      // 呼叫 OpenAI API
      const response = await firstValueFrom(
        this.openaiService.chatwithOpenAI(
          prompt,
          '', // assistantNo
          '', // response_id
          this.companyId,
          '', // toolname
          '', // vector_store_id
          'gpt-4o-mini', // model_version
          16384, // max_tokens
          [], // imageAttachments
          [] // files - 這裡可能需要傳入檔案
        )
      );

      if (response.success && response.content) {
        try {
          const parsedData = JSON.parse(response.content);

          this.aiParseResult = {
            status: 'completed',
            progress: 100,
            expiryDate: parsedData.expiryDate,
            confidence: parsedData.confidence || 0.8,
            extractedData: parsedData,
            parsedAt: new Date()
          };

          // 如果設定了有效期限欄位，觸發填入事件
          if (this.config.expiryDateField && parsedData.expiryDate) {
            this.fillExpiryDateField(parsedData.expiryDate);
          }

          this.aiParsingCompleted.emit(this.aiParseResult);
        } catch (parseError) {
          throw new Error('AI 回應格式錯誤');
        }
      } else {
        throw new Error('AI 解析失敗');
      }
    } catch (error) {
      this.aiParseResult = {
        status: 'failed',
        error: error instanceof Error ? error.message : '未知錯誤'
      };
    }

    this.emitChange();
  }

  reparseDocument(): void {
    this.startAIParsing();
  }

  private fillExpiryDateField(expiryDate: string): void {
    // 這裡需要與表單系統整合，自動填入對應的日期欄位
    // 實際實作可能需要透過事件或服務來通知表單系統
    console.log(`需要填入有效期限欄位 ${this.config.expiryDateField}: ${expiryDate}`);
  }

  // 檔案操作方法
  viewFile(): void {
    if (!this.uploadedFile?.viewUrl) return;
    window.open(this.uploadedFile.viewUrl, '_blank');
  }

  replaceFile(): void {
    this.triggerFileSelect();
  }

  removeFile(): void {
    this.removeExistingFile();
    this.fileRemoved.emit();
  }

  private async removeExistingFile(): Promise<void> {
    if (!this.uploadedFile) return;

    if (this.uploadedFile.blobName && !this.uploadedFile.uploading) {
      // 從伺服器刪除檔案
      const deleteSub = this.fileUploadService.deleteFile(this.uploadedFile.blobName).subscribe({
        next: () => console.log('檔案已從伺服器刪除:', this.uploadedFile?.name),
        error: (error) => console.error('刪除檔案失敗:', error)
      });
      this.subscriptions.push(deleteSub);
    }

    this.uploadedFile = undefined;
    this.aiParseResult = undefined;
    this.emitChange();
  }

  // 工具方法
  getAcceptedTypes(): string {
    return this.config.acceptedFormats ? this.config.acceptedFormats.join(',') : '*';
  }

  getUploadHint(): string {
    const hints = [];
    if (this.config.acceptedFormats) {
      hints.push(`支援格式: ${this.config.acceptedFormats.join(', ')}`);
    }
    if (this.config.maxFileSize) {
      hints.push(`最大 ${this.config.maxFileSize}MB`);
    }
    return this.config.uploadHint || hints.join('，');
  }

  getFileExtension(fileName: string): string {
    return '.' + fileName.split('.').pop()?.toLowerCase() || '';
  }

  getFileTypeFromExtension(fileName: string): string {
    const ext = this.getFileExtension(fileName);
    const typeMap: { [key: string]: string } = {
      '.pdf': 'application/pdf',
      '.jpg': 'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.png': 'image/png',
      '.gif': 'image/gif',
      '.doc': 'application/msword',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    };
    return typeMap[ext] || 'application/octet-stream';
  }

  getFileIcon(fileType: string): string {
    if (fileType.startsWith('image/')) {
      return 'fas fa-file-image text-success';
    } else if (fileType === 'application/pdf') {
      return 'fas fa-file-pdf text-danger';
    } else if (fileType.includes('word')) {
      return 'fas fa-file-word text-primary';
    }
    return 'fas fa-file text-secondary';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatUploadTime(date: Date): string {
    return date.toLocaleString();
  }

  isImageFile(fileType: string): boolean {
    return fileType.startsWith('image/');
  }

  onPreviewError(event: Event): void {
    if (this.uploadedFile) {
      this.uploadedFile.previewUrl = undefined;
    }
  }

  private emitChange(): void {
    const value: RequiredDocumentValue | null = this.uploadedFile ? {
      file: this.uploadedFile,
      aiParseResult: this.aiParseResult,
      lastUpdated: new Date()
    } : null;

    this.onChange(value);
    this.onTouched();
  }
}
